import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SHARED_COMPONENTS } from '../../../shared/shared-imports';
import { environment } from '../../../../environments/environment';
import { RoutesPath } from '../../../shared/constants/routes-path';
import { SharedService } from '../../../shared/services/shared.service';
import { MfaEmailOtpComponent } from "../../../shared/components/mfa-email-otp/mfa-email-otp.component";

type LoginResponse = {
  access_token?: string;
  refresh_token?: string;
  scope?: string;
  id_token?: string;
  token_type?: string;
  expires_in?: number;
  authenticatorId?: string;
  flowId?: string;
  flowStatus?: string;
  nextStep?: {
    messages?: Array<{
      context?: Array<{ key?: string; value?: string }>;
    }>;
  };
};
type UaePassInitResponse = {
  flowId: string;
  redirectUrl: string;
};
type UaePassLinkResponse = LoginResponse;

@Component({
  standalone:true,
  selector: 'app-login',
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, ...SHARED_COMPONENTS, MfaEmailOtpComponent],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css'
})
export class LoginComponent {
  loginForm: FormGroup;
  isSubmitting = false;
  isUaePassLinking = false;
  showMfaOtpModal = false;
  uaePassUser = '';
  mfaMaskedEmail = '';
  loginError = '';
  pendingMfaAuthenticatorId = '';
  pendingMfaFlowId = '';
  private pendingMfaLoginResponse: LoginResponse | null = null;
  private pendingMfaUsername = '';
  private readonly loginUrl = `${environment.wso2APIURL}DCAuthToken/AuthTokenREST/authToken`;
  private readonly uaePassInitUrl = `${environment.wso2APIURL}DCAuthToken/InitiateUAEPassAuthREST/initiateUAEPassAuth`;
  private readonly uaePassLinkUrl = `${environment.wso2APIURL}DCAuthToken/LinkUAEPassUserREST/linkUAEPassUser`;
  readonly environment = environment;
  uaePassFocused = false;
  uaePassPressed = false;
  uaePassSubmitting = false;

  // This method is used to initialize the class instance using fb, router, and oauth service
  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private http: HttpClient,
    private translate: TranslateService,
    private sharedService: SharedService
  ) {
    this.loginForm = this.fb.group({
      username: ['', Validators.required],
      password: ['', Validators.required]
    });
    localStorage.clear();
    this.sharedService.setPlatinumTheme(false);
    this.route.queryParams.subscribe((params) => {
      const uaepassuser = (params['uaepassuser'] || '').toString().trim();
      this.isUaePassLinking = !!uaepassuser;
      this.uaePassUser = uaepassuser;
      const usernameControl = this.loginForm.get('username');
      if (uaepassuser) {
        usernameControl?.setValue(uaepassuser);
        usernameControl?.disable({ emitEvent: false });
      } else {
        usernameControl?.enable({ emitEvent: false });
      }
    });
  }

  get uaePassImage(): string {
    const langDir = this.getUaePassLangDir();
    const state = this.uaePassPressed ? 'Pressed' : this.uaePassFocused ? 'Focus' : 'Active';
    return `/images/uae-pass/${langDir}/${state}@3x-2.png`;
  }

  // This method is used to log in with uae pass
  loginWithUaePass() {
    if (this.uaePassSubmitting) {
      return;
    }
    this.uaePassSubmitting = true;
    const redirectUri = environment.uaePassRedirectUri || `${window.location.origin}/${RoutesPath.PUBLIC.UAEPASS_LOGIN}`;
    this.http.post<UaePassInitResponse>(this.uaePassInitUrl, { redirect_uri: redirectUri }).subscribe({
      next: (response) => {
        if (response?.flowId) {
          sessionStorage.setItem('uaePassFlowId', response.flowId);
        }
        if (response?.redirectUrl) {
          window.location.href = response.redirectUrl;
          return;
        }
        this.uaePassSubmitting = false;
      },
      error: () => {
        this.uaePassSubmitting = false;
      }
    });
  }

  // This method is used to set UAE pass button focus
  onUaePassFocus() {
    this.uaePassFocused = true;
  }

  // This method is used to clear UAE pass button focus
  onUaePassBlur() {
    this.uaePassFocused = false;
    this.uaePassPressed = false;
  }

  // This method is used to update UAE pass button pressed state
  onUaePassPress(isPressed: boolean) {
    this.uaePassPressed = isPressed;
  }

  // This method is used to update UAE pass button pressed state via keyboard
  onUaePassKey(isPressed: boolean, event: KeyboardEvent) {
    if (event.key === ' ' || event.key === 'Enter') {
      this.uaePassPressed = isPressed;
    }
  }
  // This method is used to log in
  login() {
    this.loginError = '';
    this.isSubmitting = true;
    if (this.isUaePassLinking) {
      const rawValues = this.loginForm.getRawValue();
      const username = this.uaePassUser || rawValues.username;
      const password = rawValues.password;
      if (!password) {
        this.loginForm.markAllAsTouched();
        this.isSubmitting = false;
        return;
      }
      const flowId = sessionStorage.getItem('uaePassFlowId') || '';
      this.http.post<UaePassLinkResponse>(this.uaePassLinkUrl, {
        flowId,
        username,
        password,
        redirect_uri: environment.uaePassRedirectUri
      }).subscribe({
        next: (response) => {
          this.handleLoginSuccess(response, username);
        },
        error: () => {
          this.loginError = 'LOGIN_INVALID_TITLE';
          this.isSubmitting = false;
        },
        complete: () => {
          this.isSubmitting = false;
        }
      });
      return;
    }

    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      this.isSubmitting = false;
      return;
    }
    const { username, password } = this.loginForm.value;
    this.http.post<LoginResponse>(this.loginUrl, { username, password }).subscribe({
      next: (response) => {
        if (this.shouldPromptForMfa(response)) {
          this.pendingMfaLoginResponse = response;
          this.pendingMfaUsername = username;
          this.pendingMfaFlowId = response.flowId || '';
          this.pendingMfaAuthenticatorId = response.authenticatorId || '';
          this.mfaMaskedEmail = this.extractMaskedEmail(response);
          this.showMfaOtpModal = true;
          return;
        }
        this.handleLoginSuccess(response, username);
      },
      error: () => {
        this.loginError = 'LOGIN_INVALID_TITLE';
        this.isSubmitting = false;
      },
      complete: () => {
        this.isSubmitting = false;
      }
    });
  }

  // This method is used to navigate to account creation
  createAccount() {
    this.router.navigate(['/', RoutesPath.PUBLIC.REGISTER]);
  }

  // This method is used to navigate to username recovery
  goToForgotUsername() {
    this.router.navigate(['/', RoutesPath.PUBLIC.FORGOT_USERNAME]);
  }

  // This method is used to navigate to password recovery
  goToForgotPassword() {
    this.router.navigate(['/', RoutesPath.PUBLIC.FORGOT_PASSWORD]);
  }

  private getUaePassLangDir(): 'AR' | 'EN' {
    const lang = (this.translate.currentLang || localStorage.getItem('selectedLang') || 'en').toLowerCase();
    return lang === 'ar' ? 'AR' : 'EN';
  }

  onMfaOtpVerified(): void {
    this.showMfaOtpModal = false;
    if (!this.pendingMfaLoginResponse?.access_token) {
      this.loginError = 'LOGIN_INVALID_TITLE';
      return;
    }
    this.handleLoginSuccess(this.pendingMfaLoginResponse, this.pendingMfaUsername);
    this.clearPendingMfaState();
  }

  onMfaOtpClosed(): void {
    this.showMfaOtpModal = false;
    this.clearPendingMfaState();
  }

  private shouldPromptForMfa(response: LoginResponse): boolean {
    return !!environment.multiFactorAuthEnabled && !!response?.flowId;
  }

  private extractMaskedEmail(response: LoginResponse): string {
    const messages = response?.nextStep?.messages || [];
    for (const message of messages) {
      const context = Array.isArray(message?.context) ? message.context : [];
      const emailContext = context.find((item) => item?.key === 'maskedEmail');
      if (emailContext?.value) {
        return String(emailContext.value);
      }
    }
    return '';
  }

  private clearPendingMfaState(): void {
    this.pendingMfaFlowId = '';
    this.pendingMfaAuthenticatorId = '';
    this.mfaMaskedEmail = '';
    this.pendingMfaLoginResponse = null;
    this.pendingMfaUsername = '';
  }

  private handleLoginSuccess(response: LoginResponse, username: string): void {
    const expiresIn = Number(response?.expires_in || 0);
    const expiresAt = expiresIn ? Date.now() + expiresIn * 1000 : null;
    const userData = {
      user: { username },
      access_token: response.access_token,
      refresh_token: response.refresh_token,
      id_token: response.id_token,
      token: response.id_token || response.access_token,
      token_type: response.token_type,
      scope: response.scope,
      expiresAt
    };
    localStorage.setItem('login_user', JSON.stringify(userData));
    this.router.navigate([RoutesPath.DASHBOARD]);
  }
}
